import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { Marker } from '@vis.gl/react-maplibre';
import { useAppSelector } from 'store/hook';
import { selectLatestTagData } from 'store/device.slice';
import { selectAssets } from 'store/asset.slice';
import { images } from 'components/Asset/PNG';
import { getImageName } from 'utils/helpers/mapAssetImage';
import { type Asset } from 'types/asset.types';

// Same canvas-drawn bumper label + PNG icon used by
// LocationBoundaryDrawer/BoundaryMapPicker, so assets look consistent
// everywhere they're shown on a map in this app. Pure canvas drawing with no
// Cesium dependency, so this ports over unchanged from the Cesium version.
function buildTruckLabel(bumperNumber: string): string {
  const font = 'bold 14px sans-serif';
  const horizontalPadding = 10;
  const measureCanvas = document.createElement('canvas');
  const measureCtx = measureCanvas.getContext('2d');
  const textWidth = measureCtx
    ? (() => {
        measureCtx.font = font;
        return measureCtx.measureText(bumperNumber).width;
      })()
    : bumperNumber.length * 9;

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(textWidth) + horizontalPadding * 2;
  canvas.height = 34;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  ctx.fillStyle = 'rgba(10,15,30,0.88)';
  ctx.strokeStyle = '#94a3b8';
  ctx.lineWidth = 1.5;
  const r = 5;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(canvas.width - r, 0);
  ctx.quadraticCurveTo(canvas.width, 0, canvas.width, r);
  ctx.lineTo(canvas.width, canvas.height - r);
  ctx.quadraticCurveTo(
    canvas.width,
    canvas.height,
    canvas.width - r,
    canvas.height
  );
  ctx.lineTo(r, canvas.height);
  ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.font = font;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(bumperNumber, horizontalPadding, canvas.height / 2);
  return canvas.toDataURL();
}

// buildTruckLabel is a pure function of bumperNumber alone — cache the
// rendered data URL per bumper so re-renders (e.g. only lat/lon changing)
// don't redo a canvas draw + toDataURL() for every truck every time.
const truckLabelCache = new Map<string, string>();
function getTruckLabelImage(bumperNumber: string): string {
  const cached = truckLabelCache.get(bumperNumber);
  if (cached) return cached;
  const image = buildTruckLabel(bumperNumber);
  truckLabelCache.set(bumperNumber, image);
  return image;
}

// Exact same call Fleet Overview's AssetMap makes for its truck icon, so
// an asset renders identically here.
function getAssetPng(asset: Asset): string {
  return images[getImageName(asset, 'Small')] ?? '';
}

// Live truck/asset markers for the Mine Map Studio map — self-contained,
// reads only Redux (no state written by any other Mine Map Studio concern).
//
// Ported from Cesium Entity+billboard pairs (one billboard for the asset
// icon, one for the bumper-number label, both anchored to the same geo
// point with pixelOffset) to a single MapLibre <Marker> per truck stacking
// the label above the icon in a column — the same "label above icon,
// anchored at the ground point" layout, done the same way DispatchMap.tsx
// stacks its truck markers instead of trying to reproduce Cesium's exact
// pixelOffset values (which depended on Cesium's per-billboard scale).
export function useTruckMarkers(): JSX.Element[] {
  const latestTagData = useAppSelector(selectLatestTagData);
  const assets = useAppSelector(selectAssets);

  // device -> { bumperNumber, raw asset }
  const assetInfoMap = useMemo<
    Record<string, { bumper: string; raw: Asset }>
  >(() => {
    const map: Record<string, { bumper: string; raw: Asset }> = {};
    assets.forEach((a) => {
      if (a.device?.deviceId) {
        map[a.device.deviceId] = {
          bumper: a.bumperNumber ?? a.device.deviceId,
          raw: a,
        };
      }
    });
    return map;
  }, [assets]);

  const truckDots = useMemo(() => {
    return Object.entries(latestTagData)
      .map(([deviceId, s]) => {
        const lat = Number(s.gps_latitude);
        const lon = Number(s.gps_longitude);
        if (!isFinite(lat) || !isFinite(lon) || lat === 0 || lon === 0)
          return null;
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
        const info = assetInfoMap[deviceId];
        return {
          deviceId,
          lat,
          lon,
          bumper: info?.bumper ?? deviceId,
          asset: info?.raw,
        };
      })
      .filter((d): d is NonNullable<typeof d> => d !== null);
  }, [latestTagData, assetInfoMap]);

  return useMemo(() => {
    return truckDots.map((dot) => (
      <Marker
        key={dot.deviceId}
        longitude={dot.lon}
        latitude={dot.lat}
        anchor="bottom"
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            pointerEvents: 'none',
          }}
        >
          <Box
            component="img"
            src={getTruckLabelImage(dot.bumper)}
            sx={{ height: 20, mb: '2px' }}
          />
          {dot.asset && (
            <Box
              component="img"
              src={getAssetPng(dot.asset)}
              sx={{ height: 28 }}
            />
          )}
        </Box>
      </Marker>
    ));
  }, [truckDots]);
}
